// Database service for managing chess openings and lines
// Using API calls to Express server with JSON file backend

class ChessDatabase {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL;
    this.isInitialized = false;
  }

  // Initialize database connection
  async init() {
    try {
      if (!this.isInitialized) {
        // Test connection to server by trying to fetch openings
        const response = await fetch(`${this.baseURL}/openings`);
        if (response.ok || response.status === 404) {
          this.isInitialized = true;
          console.log('Database connection established');
        } else {
          throw new Error('Database server not responding');
        }
      }
    } catch (error) {
      console.error('Database initialization failed:', error);
      throw error;
    }
  }

  // Helper method for API calls
  async apiCall(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const validationErrors = errorData.errors ? Object.values(errorData.errors).flat().join(' ') : null;
      throw new Error(validationErrors || errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  // Opening CRUD operations
  async getAllOpenings() {
    return this.apiCall('/openings');
  }

  async getOpeningById(id) {
    return this.apiCall(`/openings/${id}`);
  }

  async createOpening(opening) {
    return this.apiCall('/openings', {
      method: 'POST',
      body: JSON.stringify(opening),
    });
  }

  async updateOpening(id, updates) {
    return this.apiCall(`/openings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteOpening(id) {
    return this.apiCall(`/openings/${id}`, {
      method: 'DELETE',
    });
  }

  // Create a community course with its lines
  async createCourse(course) {
    return this.apiCall('/openings', {
      method: 'POST',
      body: JSON.stringify(course),
    });
  }

  async likeOpening(id) {
    return this.apiCall(`/openings/${id}/like`, { method: 'POST' });
  }

  async unlikeOpening(id) {
    return this.apiCall(`/openings/${id}/like`, { method: 'DELETE' });
  }

  // Line CRUD operations
  async getAllLines() {
    return this.apiCall('/lines');
  }

  async getLinesByOpeningId(openingId) {
    return this.apiCall(`/lines?openingId=${openingId}`);
  }

  async getLineById(id) {
    return this.apiCall(`/lines/${id}`);
  }

  async createLine(line) {
    return this.apiCall('/lines', {
      method: 'POST',
      body: JSON.stringify(line),
    });
  }

  async updateLine(id, updates) {
    return this.apiCall(`/lines/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteLine(id) {
    return this.apiCall(`/lines/${id}`, {
      method: 'DELETE',
    });
  }

  // Get openings with their lines
  async getOpeningsWithLines() {
    try {
      const openings = await this.getAllOpenings();
      const allLines = await this.getAllLines();
      
      return openings.map(opening => ({
        ...opening,
        // Convert snake_case to camelCase for frontend consistency
        playerColor: opening.player_color,
        authorName: opening.author_name,
        isCommunity: !!opening.is_community,
        likesCount: opening.likes_count || 0,
        lines: allLines.filter(line => line.opening_id === opening.id).map(line => ({
          ...line,
          openingId: line.opening_id,
          description: line.information
        }))
      }));
    } catch (error) {
      console.error('Error getting openings with lines:', error);
      return [];
    }
  }
}

// Create and export database instance
const database = new ChessDatabase();
export default database;